import { React, moment } from 'Imports';
import { isEncompass } from '$Utilities/integrationPlatform';
import { useEffect, useRef, useState } from 'react';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import VideoPlayer from '$Components/VideoViews/VideoPlayer/VideoPlayer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBackwardStep, faForwardStep } from '@fortawesome/pro-solid-svg-icons';
import FullscreenView from '$Components/VideoViews/Layouts/FullscreenView';
import SingleSelect from '$Components/FilterComponents/Filters/SingleSelect';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { CameraSegmentBase, CameraSegmentFullData } from '../TimeAndDurationStep/FootageSegments';
import { useVideoPlayerSync } from '$State/VideoPlayerSync/VideoPlayerSyncContext';
import RecallTimeInput, { ParsedRecallTime } from './RecallTimeInput';

const styles = require('./VideoPreview.scss') as {
    geotab: string;
    encompass: string;
    titleAndTimeWrapper: string;
    title: string;
    playerWrapper: string;
    outOfRangeOverlay: string;
    timezoneAndNavButtonWrapper: string;
    navButtonWrapper: string;
    navButton: string;
    navButtonIcon: string;
    divider: string;
    fieldsRow: string;
    fieldColumn: string;
    fieldLabel: string;
    timeErrorText: string;
    disclaimer: string;
};

interface VideoPreviewProps {
    fileUrl: string | null;
    cameraSegment: CameraSegmentFullData;
    // IANA zone the footage was recorded in (fleet-wide video overlay timezone); null when not configured.
    fleetTimezone: string | null;
    recallStartDate?: Date;
    selectedDuration: number | undefined;
    durationOptions: IDropdownItem[];
    onSegmentChange: (segment: CameraSegmentBase) => void;
    onRecallStartDateChange: (date: Date | undefined) => void;
    onDurationChange: (duration: number | undefined) => void;
    // Reports whether the typed recall time is currently invalid (a future instant) so the parent can
    // disable Submit without this component having to clear the actual recall time/hand control away
    // from the video to do so.
    onValidationErrorChange?: (hasError: boolean) => void;
}

// The preview clips are timelapses: N frames shown over the playback duration, and those N frames span only
// (N - 1) inter-frame intervals of real time -- frame 0 sits at segmentStart and the last frame at segmentEnd.
// So the real-time fraction is frameIndex / (N - 1), NOT currentTime / duration; the latter divides by the
// frame COUNT instead of the interval count and under-shoots by up to one frame interval (severe on short
// clips with few frames, negligible on long ones). The frame rate is measured per clip (VideoPlayer's
// requestVideoFrameCallback); this is only the fallback used until/unless that measurement is available
// (the clips are 1 fps the vast majority of the time, but it isn't guaranteed).
const FALLBACK_FRAMES_PER_SECOND = 1;

const floorToSecond = (ms: number) => Math.floor(ms / 1000) * 1000;

// Video-driven selections round DOWN to a whole minute (clamped to the segment start): the frame-to-real-time
// mapping is only approximate, and flooring biases the recall start to at-or-before the viewed moment so the
// footage of interest is always included. Typed times keep their full second precision.
const floorToMinuteWithin = (ms: number, minMs: number) => Math.max(Math.floor(ms / 60000) * 60000, minMs);

const VideoPreview = (props: VideoPreviewProps) => {
    const { fileUrl, cameraSegment, fleetTimezone } = props;
    const mainClass = isEncompass ? styles.encompass : styles.geotab;

    const { isFullView, setIsFullView, setDisableSync } = useVideoPlayerSync();
    const [timeError, setTimeError] = useState(false);
    // True when the currently accepted recall time, together with the selected duration, would end in the
    // future -- a real request that can never correspond to existing footage, so (unlike outOfRange below)
    // it isn't submittable at all until corrected. Derived reactively from recallStartDate/selectedDuration
    // (see the effect below) rather than decided once at the moment of typing, so it also reacts correctly
    // to a duration change made after a valid time was already accepted -- or a video position that, paired
    // with a long enough duration, would itself end in the future.
    const [futureTimeError, setFutureTimeError] = useState(false);
    // The typed recall time falls outside the previewed segment: the player grays out (there is no frame to
    // show for that time) until the user picks an in-range time or clicks the overlay to reset.
    const [outOfRange, setOutOfRange] = useState(false);

    const segmentStartMs = moment(cameraSegment.startTime).valueOf();
    const segmentEndMs = moment(cameraSegment.endTime).valueOf();
    // Times are entered and displayed in the footage's zone. When the fleet setting is unavailable the
    // zone is genuinely unknown, so fall back to the user's zone for interpretation but suppress every
    // claim about the footage's zone (input suffix, "Footage reflects", conversion banner) rather than
    // presenting the user's own zone as the footage's.
    const footageTimezone = fleetTimezone ?? moment.tz.guess();

    // Latest clip metrics, captured from playback progress so typed times can be mapped back onto the video.
    const clipDurationRef = useRef<number | null>(null);
    const clipFpsRef = useRef<number | null>(null);
    // Last real-world time (ms) pushed up -- throttles per-frame progress into once-per-minute updates.
    // Starts at null (rather than segmentStart) so the first frame reported for a freshly loaded/changed
    // segment always emits -- the parent no longer prefills a value, so there's no duplicate to swallow.
    const lastEmittedMsRef = useRef<number | null>(null);
    // While a typed-time seek is in flight, its target frame's mediaTime. Progress events are swallowed
    // until the sought frame is presented, so the echo of our own seek doesn't overwrite the typed value.
    const pendingSeekRef = useRef<{ mediaTime: number; misses: number } | null>(null);
    // Set while the recall time is being driven by the input rather than the video, i.e. the typed time is
    // outside the segment so no frame corresponds to it. Playback progress is ignored until control is
    // handed back (an in-range entry, the overlay reset, or a segment change). Pausing alone is not enough:
    // vidstack queues pause requests, so frames presented before the pause lands would otherwise overwrite
    // the typed time and dismiss the overlay.
    const inputDrivenRef = useRef(false);
    const seekRef = useRef<((time: number) => void) | null>(null);
    const pauseRef = useRef<(() => void) | null>(null);
    // Raw (unmapped) playback position of the small player, in video seconds -- not the mapped real-world
    // recall time. Used only to seed the fullscreen player's starting position when it opens, so it picks
    // up where the small player left off instead of always restarting at 0.
    const smallPlayerTimeRef = useRef(0);

    useEffect(() => {
        setDisableSync(true);
    }, []);

    useEffect(() => {
        lastEmittedMsRef.current = null;
        pendingSeekRef.current = null;
        inputDrivenRef.current = false;
        setTimeError(false);
        setOutOfRange(false);
    }, [segmentStartMs, segmentEndMs]);

    // Re-derives the future-ending check whenever either input changes -- covers a typed or played/scrubbed
    // recall start, a duration picked after the fact, and recovers automatically the moment either one no
    // longer ends in the future (a shorter duration, a corrected time, or continued playback).
    useEffect(() => {
        if (props.recallStartDate === undefined || props.selectedDuration === undefined) {
            setFutureTimeError(false);
            return;
        }
        const endMs = props.recallStartDate.getTime() + props.selectedDuration * 1000;
        setFutureTimeError(endMs > moment().valueOf());
    }, [props.recallStartDate, props.selectedDuration]);

    useEffect(() => {
        props.onValidationErrorChange?.(futureTimeError);
        // This component unmounts while the next segment's preview is loading (or when the step is left
        // entirely) -- clear a lingering error report rather than leaving the parent stuck with Submit
        // disabled for a component that's no longer here to correct it.
        return () => props.onValidationErrorChange?.(false);
    }, [futureTimeError]);

    // Video -> recall time: maps the CURRENT FRAME (not raw playback time) onto the segment; frame 0 ->
    // segmentStart, last frame -> segmentEnd, with (frameCount - 1) intervals between them.
    // `source` distinguishes the small preview player from the fullscreen player so the two can share this
    // mapping: only 'main' progress is subject to the pending-seek echo guard below (that guard exists to
    // swallow the small player's own transitional frames while it settles into a seek WE issued -- the
    // fullscreen player is never seeked programmatically, so its progress is always live and must not be
    // throttled by that guard, or continuous fullscreen scrubbing/playback would stall for several frames
    // every time it nudges the small player -- see the 'fullscreen' branch below).
    const handlePlaybackProgress = (
        currentTime: number,
        duration: number,
        framesPerSecond?: number,
        source: 'main' | 'fullscreen' = 'main',
    ) => {
        if (source === 'main') smallPlayerTimeRef.current = currentTime;

        clipDurationRef.current = duration;
        if (framesPerSecond && framesPerSecond > 0) clipFpsRef.current = framesPerSecond;
        const fps = clipFpsRef.current ?? FALLBACK_FRAMES_PER_SECOND;

        // The input owns the recall time while it holds an out-of-range value; frames presented in the
        // meantime (the video may still be settling into its pause) must not overwrite it.
        if (inputDrivenRef.current) return;

        if (source === 'main' && pendingSeekRef.current) {
            const isTargetFrame = Math.abs(currentTime - pendingSeekRef.current.mediaTime) <= 0.6 / fps;
            // Swallow the echo of our own typed-time (or fullscreen-sync) seek, and any stale in-flight
            // frames before it.
            if (isTargetFrame || ++pendingSeekRef.current.misses > 5) pendingSeekRef.current = null;
            if (isTargetFrame || pendingSeekRef.current) return;
        }

        if (duration <= 0) return;

        const frameCount = Math.max(1, Math.round(duration * fps));
        const frameIndex = Math.min(frameCount - 1, Math.max(0, Math.floor(currentTime * fps)));
        const percent = frameCount > 1 ? frameIndex / (frameCount - 1) : 0;
        const realWorldMs = floorToMinuteWithin(segmentStartMs + percent * (segmentEndMs - segmentStartMs), segmentStartMs);

        if (lastEmittedMsRef.current === realWorldMs) return;
        lastEmittedMsRef.current = realWorldMs;
        setTimeError(false);
        setOutOfRange(false);
        props.onRecallStartDateChange(new Date(realWorldMs));

        // Mirror the fullscreen player's position onto the small player so it's left in sync once fullscreen
        // closes. The small player is paused while fullscreen is open (see VideoPlayer's fullscreen toggle),
        // so this can't fight with its own playback.
        if (source === 'fullscreen') seekVideoToMs(realWorldMs);
    };

    // Recall time -> video: seek the clip to the typed time's frame (inverse of the mapping above). Rounds
    // UP to the first frame at-or-after the typed time, mirroring how video-driven selection rounds down --
    // the shown frame never predates the selected recall start, so the two stay consistent.
    const seekVideoToMs = (targetMs: number) => {
        const duration = clipDurationRef.current;
        if (!duration || duration <= 0 || !seekRef.current) return;
        const fps = clipFpsRef.current ?? FALLBACK_FRAMES_PER_SECOND;
        const frameCount = Math.max(1, Math.round(duration * fps));
        const percent = segmentEndMs > segmentStartMs ? (targetMs - segmentStartMs) / (segmentEndMs - segmentStartMs) : 0;
        const frameIndex = Math.min(frameCount - 1, Math.max(0, Math.ceil(percent * (frameCount - 1))));
        pendingSeekRef.current = { mediaTime: frameIndex / fps, misses: 0 };
        // Aim for the middle of the frame so decoder rounding can't land on the previous frame.
        seekRef.current(Math.min((frameIndex + 0.5) / fps, Math.max(0, duration - 0.05)));
    };

    // Resolves a typed HH:MM:SS AM/PM (in the footage timezone) to an absolute instant. A clock time alone
    // doesn't pin the calendar day, so of the calendar day(s) the segment itself spans (2 only when it
    // crosses midnight in the footage zone -- normally just 1), pick whichever instant is closest to the
    // segment's midpoint. This is deliberately scoped to the segment's own day(s), not an arbitrary +/-1
    // window: candidate days further out can be numerically "closer" to the midpoint than the segment's
    // actual day for times far outside the segment (e.g. 11:11 PM against a 9:56-11:56 AM segment is closer
    // to the midpoint on the PRECEDING day than on the segment's own day), which would silently resolve an
    // intended future time to one in the past.
    const typedTimeToMs = (time: ParsedRecallTime): number => {
        let hour24 = time.hour % 12;
        if (time.amPm === 'PM') hour24 += 12;
        const segmentStartDay = moment(cameraSegment.startTime).tz(footageTimezone).startOf('day');
        const segmentEndDay = moment(cameraSegment.endTime).tz(footageTimezone).startOf('day');
        const daySpan = segmentEndDay.diff(segmentStartDay, 'days');
        const segmentMidpointMs = (segmentStartMs + segmentEndMs) / 2;
        const candidates = [];
        for (let dayOffset = 0; dayOffset <= daySpan; dayOffset++) {
            candidates.push(segmentStartDay.clone().add(dayOffset, 'day').hour(hour24).minute(time.minute).second(time.second).valueOf());
        }
        return candidates.reduce((best, candidate) =>
            Math.abs(candidate - segmentMidpointMs) < Math.abs(best - segmentMidpointMs) ? candidate : best,
        );
    };

    const handleTimeChange = (time: ParsedRecallTime | undefined) => {
        if (!time) {
            // Incomplete entry while editing -- disable submit but don't flag an error yet (blur will).
            // There is no longer an out-of-range value to explain, so drop the overlay and hand the video
            // back control rather than showing the overlay and the invalid-time message at once.
            inputDrivenRef.current = false;
            setOutOfRange(false);
            setTimeError(false);
            props.onRecallStartDateChange(undefined);
            return;
        }
        setTimeError(false);
        const targetMs = typedTimeToMs(time);
        const flooredMs = floorToSecond(targetMs);
        // Always accept what was typed -- including a future one -- rather than withholding it: the future
        // check (below and in the reactive effect above) disables Submit on its own, and withholding it here
        // would make the field revert to the video's position the moment the user blurs it, before they've
        // even had a chance to read the validation error.
        lastEmittedMsRef.current = flooredMs;
        props.onRecallStartDateChange(new Date(flooredMs));

        const durationMs = (props.selectedDuration ?? 0) * 1000;
        if (flooredMs + durationMs > moment().valueOf()) {
            // No frame corresponds to a future instant -- leave the video/input-driven state untouched
            // (playing or scrubbing, retyping, or picking a shorter duration is how the user recovers) and
            // skip the out-of-range overlay below, whose reset-to-start messaging doesn't apply here.
            return;
        }

        // Out-of-range times are still submittable, but there is no frame to show for them: gray the
        // player out instead of seeking (pausing so playback can't keep re-driving the recall time).
        const inRange = targetMs >= segmentStartMs && targetMs <= segmentEndMs;
        inputDrivenRef.current = !inRange;
        setOutOfRange(!inRange);
        if (inRange) {
            seekVideoToMs(targetMs);
        } else {
            pendingSeekRef.current = null;
            pauseRef.current?.();
        }
    };

    // Clicking the grayed-out player scrubs back to the start of the preview range (and re-syncs the
    // recall time to it).
    const handleOutOfRangeOverlayClick = () => {
        inputDrivenRef.current = false;
        setOutOfRange(false);
        setTimeError(false);
        lastEmittedMsRef.current = floorToSecond(segmentStartMs);
        props.onRecallStartDateChange(new Date(floorToSecond(segmentStartMs)));
        seekVideoToMs(segmentStartMs);
    };

    const handleTimeEditEnd = (time: ParsedRecallTime | undefined) => {
        if (!time) setTimeError(true);
    };

    const handleDurationChange = (item: IDropdownItem | null) => {
        if (item) {
            props.onDurationChange(item.value as number);
        }
    };

    // Resolved at the segment's instant, not "now", so footage recorded on the other side of a DST
    // boundary is labelled with the abbreviation that was actually in effect when it was recorded.
    // Null when the footage zone is unknown -- see fleetTimezone above.
    const footageTimezoneLabel = fleetTimezone
        ? (() => {
              const parts = (dt: Intl.DateTimeFormatOptions['timeZoneName']) =>
                  new Intl.DateTimeFormat('en-US', { timeZoneName: dt, timeZone: fleetTimezone })
                      .formatToParts(moment(cameraSegment.startTime).toDate())
                      .find((p) => p.type === 'timeZoneName')?.value ?? '';
              const full = parts('long');
              const abbr = parts('short');
              return full && abbr ? `${full} (${abbr})` : full || abbr || null;
          })()
        : null;

    const currentIndex = cameraSegment.groupSegments.findIndex(
        (s) => s.startTime === cameraSegment.startTime && s.endTime === cameraSegment.endTime,
    );

    return (
        <div className={mainClass}>
            <div className={styles.titleAndTimeWrapper}>
                <div className={styles.title}>{cameraSegment.segmentTitle}</div>
                <div>{`${DateFormatter.getTime(moment(cameraSegment.startTime))} - ${DateFormatter.timeWithTimezone(moment(cameraSegment.endTime))}`}</div>
            </div>
            {fileUrl && (
                <div className={styles.playerWrapper}>
                    <div style={{ backgroundColor: '#000' }}>
                        <VideoPlayer
                            src={fileUrl}
                            videoType="FrontVideo"
                            downloadable={false}
                            fullscreenable={true}
                            onOpenFullScreen={() => {
                                setIsFullView(true);
                            }}
                            onPlaybackProgress={handlePlaybackProgress}
                            seekRef={seekRef}
                            pauseRef={pauseRef}
                            maxHeight={250}
                        />
                    </div>
                    {outOfRange && (
                        <div className={styles.outOfRangeOverlay} onClick={handleOutOfRangeOverlayClick}>
                            <div>Recall start time is out of preview range.</div>
                            <div>Click here to reset to the beginning.</div>
                        </div>
                    )}
                </div>
            )}
            <div className={styles.timezoneAndNavButtonWrapper}>
                {footageTimezoneLabel && <div>Footage reflects {footageTimezoneLabel}</div>}
                <div className={styles.navButtonWrapper}>
                    <button
                        type="button"
                        className={styles.navButton}
                        disabled={currentIndex <= 0}
                        onClick={() => props.onSegmentChange(cameraSegment.groupSegments[currentIndex - 1])}
                    >
                        <FontAwesomeIcon icon={faBackwardStep} className={styles.navButtonIcon} />
                    </button>
                    <button
                        type="button"
                        className={styles.navButton}
                        disabled={currentIndex >= cameraSegment.groupSegments.length - 1}
                        onClick={() => props.onSegmentChange(cameraSegment.groupSegments[currentIndex + 1])}
                    >
                        <FontAwesomeIcon icon={faForwardStep} className={styles.navButtonIcon} />
                    </button>
                </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.fieldsRow}>
                <div className={styles.fieldColumn}>
                    <div className={styles.fieldLabel}>Recall Start Time</div>
                    <RecallTimeInput
                        value={props.recallStartDate}
                        timezone={footageTimezone}
                        abbreviationAt={fleetTimezone ? moment(cameraSegment.startTime).toDate() : undefined}
                        error={timeError || futureTimeError}
                        onTimeChange={handleTimeChange}
                        onEditEnd={handleTimeEditEnd}
                    />
                </div>
                <div className={styles.fieldColumn}>
                    <div className={styles.fieldLabel}>Recall Duration</div>
                    <SingleSelect
                        hideLabel
                        label=""
                        disabled={props.durationOptions.length === 0}
                        isMobileView={false}
                        selectionChanged={handleDurationChange}
                        selectedValue={props.selectedDuration}
                        items={props.durationOptions}
                        sort={false}
                        placeholder="Select duration"
                        inputHeight={isEncompass ? 36 : 40}
                    />
                </div>
            </div>
            {timeError && <div className={styles.timeErrorText}>Time is invalid, enter a valid time</div>}
            {!timeError && futureTimeError && <div className={styles.timeErrorText}>Time cannot be in the future.</div>}
            <div className={styles.disclaimer}>Time selection from preview is approximate. Please review before submitting.</div>
            {fileUrl && isFullView && (
                <FullscreenView
                    primaryChannelNumber={1}
                    numberOfChannels={1}
                    videoAssets={[{ url: fileUrl, channelNumber: 1, type: 'FrontVideo' }]}
                    onCloseFullScreen={() => setIsFullView(false)}
                    downloadable={false}
                    initialTimeSeconds={smallPlayerTimeRef.current}
                    onPlaybackProgress={(currentTime, duration, framesPerSecond) =>
                        handlePlaybackProgress(currentTime, duration, framesPerSecond, 'fullscreen')
                    }
                />
            )}
        </div>
    );
};

export default VideoPreview;
